import {
	baseApiHeaders,
	ENV_TARGET_KEY,
	FIXEDCACHEKEYS,
	HIDE_TOAST_KEY,
	type IApiResponse,
	type ILoginResponse,
	type IRedeemEnvExchange,
	type IRegister,
	type ISignupBusinessReqData,
	type IUser,
	type IUserBusiness,
	type OTPTYPE,
	type TargetEnvTypes,
} from 'BreetHelpers';

import { apiSlice } from './apiSlice';

export const authApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		signupBusiness: builder.mutation<IApiResponse<IRegister>, ISignupBusinessReqData>({
			query: (body) => ({
				method: 'POST',
				url: '/auth/signup/business',
				body,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
		}),
		emailVerification: builder.mutation<IApiResponse<IUser>, { code: string; token: string }>({
			query: (body) => ({
				method: 'PATCH',
				url: '/auth/verification',
				body,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
		}),
		login: builder.mutation<
			IApiResponse<ILoginResponse>,
			{ email: string; password?: string; otp?: string | { code?: string; type?: OTPTYPE }; businessId?: string }
		>({
			query: (body) => ({
				method: 'POST',
				url: '/auth/login/vip',
				body,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
		}),
		logout: builder.mutation<IApiResponse, void>({
			query: () => ({
				method: 'DELETE',
				url: '/auth/logout/user',
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
		}),
		forgotPassword: builder.mutation<IApiResponse<{ token: string }>, { email: string }>({
			query: (body) => ({
				method: 'POST',
				url: '/auth/forgot-password',
				body,
			}),
		}),
		resendVerification: builder.mutation<IApiResponse<{ email: string; token: string }>, { email: string }>({
			query: (body) => ({
				method: 'POST',
				url: '/auth/verification',
				body,
			}),
		}),
		verifyOTPAuth: builder.mutation<IApiResponse, { token: string; otp: string }>({
			query: (body) => ({
				method: 'PUT',
				url: '/auth/verify-otp',
				body,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
		}),
		verifyPassword: builder.mutation<IApiResponse, { password: string }>({
			query: (body) => ({
				method: 'PUT',
				url: '/users/user/password',
				body,
			}),
		}),
		changePassword: builder.mutation<IApiResponse, { newPassword: string; oldPassword: string }>({
			query: (body) => ({
				method: 'PATCH',
				url: '/users/user/password',
				body,
			}),
		}),
		resetPassword: builder.mutation<IApiResponse, { token: string; password: string }>({
			query: (body) => ({
				method: 'PUT',
				url: '/auth/reset-password',
				body,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
		}),
		requestEnvExchange: builder.mutation<
			IApiResponse<{
				exchangeToken: string;
			}>,
			{
				targetEnv: TargetEnvTypes;
			}
		>({
			query: (body) => ({
				method: 'POST',
				url: '/auth/env-exchange',
				body,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
		}),
		redeemEnvExchange: builder.mutation<
			IApiResponse<IRedeemEnvExchange>,
			{
				exchangeToken: string;
				targetEnv: TargetEnvTypes;
			}
		>({
			query: ({ exchangeToken, targetEnv }) => ({
				method: 'POST',
				url: '/auth/env-redeem',
				body: { exchangeToken },
				headers: { [HIDE_TOAST_KEY]: 'true', [ENV_TARGET_KEY]: targetEnv },
			}),
		}),
		getBusiness: builder.query<IApiResponse<IUserBusiness>, string | undefined>({
			query: () => ({ url: '/auth/businesses' }),
		}),
		switchBusiness: builder.mutation<IApiResponse, { businessId: string }>({
			query: (body) => ({
				method: 'POST',
				url: '/auth/businesses/switch',
				body,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
			invalidatesTags: ['User'],
			async onQueryStarted(_, { dispatch, queryFulfilled }) {
				try {
					await queryFulfilled;
					dispatch(apiSlice.util.resetApiState());
				} catch {
					// switch failed — leave cache intact
				}
			},
		}),
	}),
});

export const {
	useForgotPasswordMutation,
	useVerifyOTPAuthMutation,
	useResetPasswordMutation,
	useResendVerificationMutation,
	useLoginMutation,
	useVerifyPasswordMutation,
	useLogoutMutation,
	useChangePasswordMutation,
	useSignupBusinessMutation,
	useEmailVerificationMutation,
	useRedeemEnvExchangeMutation,
	useRequestEnvExchangeMutation,
	useGetBusinessQuery,
	useSwitchBusinessMutation,
} = authApiSlice;

export const selectLogin = authApiSlice.endpoints.login.select(FIXEDCACHEKEYS.LOGIN);
